import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { AgGridReact } from "@ag-grid-community/react";
import type { ColDef, ColumnMovedEvent, RowDragEndEvent } from "@ag-grid-community/core";
import type { FacetTableData } from "./facet-table-helpers";
import { reorderTableColumns } from "./facet-table-sort-helpers";
import { rowsAfterRowDragEvent, tableContentKey } from "./facet-table-drag-order";
import {
    buildFacetResultTableColDefs,
    FACET_RESULT_TABLE_DEFAULT_COL_DEF,
} from "./facet-result-table-grid-helpers";

function buildViewColDefs(headers: string[], allowColumnReorder: boolean): ColDef[] {
    return headers.map((header, idx) => ({
        field: header,
        rowDrag: idx === 0,
        suppressMovable: idx === 0 || !allowColumnReorder,
        headerName: header.trim() || `Column ${idx + 1}`,
    }));
}

interface FacetResultTableGridProps {
    table: FacetTableData;
    onRowOrderChange?: (rowLabels: string[]) => void;
    onColumnOrderChange?: (columnHeaders: string[]) => void;
    sortOrderPersistDisabled?: boolean;
    allowColumnReorder?: boolean;
    isEditing?: boolean;
    onBatchEditingStopped?: (event: { edits?: unknown[] }) => void;
}

const FacetResultTableGrid = forwardRef<AgGridReact, FacetResultTableGridProps>(function FacetResultTableGrid(
    {
        table,
        onRowOrderChange,
        onColumnOrderChange,
        sortOrderPersistDisabled = false,
        allowColumnReorder = true,
        isEditing = false,
        onBatchEditingStopped,
    },
    ref
) {
    const [localTable, setLocalTable] = useState(table);
    const localRowsRef = useRef(localTable.rows);
    localRowsRef.current = localTable.rows;
    const rowLabelFieldRef = useRef(localTable.headers[0] ?? "");
    rowLabelFieldRef.current = localTable.headers[0] ?? "";

    const serverKey = useMemo(() => tableContentKey(table), [table.headers, table.rows]);

    useEffect(() => {
        if (isEditing || sortOrderPersistDisabled) return;
        setLocalTable(table);
    }, [serverKey, table, sortOrderPersistDisabled, isEditing]);

    const colDefs = useMemo(
        () =>
            isEditing
                ? buildFacetResultTableColDefs(localTable.headers, true)
                : buildViewColDefs(localTable.headers, allowColumnReorder),
        [localTable.headers, allowColumnReorder, isEditing]
    );

    const handleRowDragEnd = useCallback(
        (event: RowDragEndEvent) => {
            if (isEditing || sortOrderPersistDisabled || !onRowOrderChange || !rowLabelFieldRef.current) return;
            const api = event.api;
            const labelField = rowLabelFieldRef.current;
            const currentRows = localRowsRef.current;

            requestAnimationFrame(() => {
                const { rows, labels } = rowsAfterRowDragEvent(
                    { ...event, api } as RowDragEndEvent,
                    labelField,
                    currentRows
                );
                if (labels.length === 0) return;
                flushSync(() => {
                    setLocalTable((prev) => ({ ...prev, rows }));
                });
                queueMicrotask(() => onRowOrderChange(labels));
            });
        },
        [isEditing, onRowOrderChange, sortOrderPersistDisabled]
    );

    const handleColumnMoved = useCallback(
        (event: ColumnMovedEvent) => {
            if (isEditing || sortOrderPersistDisabled || !onColumnOrderChange || event.finished !== true) return;
            const headers =
                event.api
                    .getAllDisplayedColumns()
                    ?.map((col) => col.getColDef().field ?? "")
                    .filter((field) => field !== "") ?? [];
            flushSync(() => {
                setLocalTable((prev) => reorderTableColumns(prev, headers));
            });
            queueMicrotask(() => onColumnOrderChange(headers));
        },
        [isEditing, onColumnOrderChange, sortOrderPersistDisabled]
    );

    return (
        <AgGridReact
            ref={ref}
            rowData={isEditing ? table.rows : localTable.rows}
            columnDefs={colDefs}
            defaultColDef={FACET_RESULT_TABLE_DEFAULT_COL_DEF}
            domLayout="autoHeight"
            suppressHorizontalScroll
            suppressCellFocus={!isEditing}
            animateRows
            rowDragManaged={!isEditing}
            onRowDragEnd={isEditing ? undefined : handleRowDragEnd}
            onColumnMoved={!isEditing && allowColumnReorder ? handleColumnMoved : undefined}
            onBatchEditingStopped={onBatchEditingStopped}
        />
    );
});

export default FacetResultTableGrid;
